// Wrapper around fetch that adds Azure AD Bearer token and audit user headers
// Also installs a global fetch interceptor so ALL API calls are authenticated

// Singleton MSAL instance for token acquisition
let msalInstance = null;

export function setMsalInstance(instance) {
  msalInstance = instance;
  // Install global fetch interceptor once we have the MSAL instance
  installFetchInterceptor();
}

export function getAuditUser() {
  return {
    name: localStorage.getItem('auditUserName') || '',
    email: localStorage.getItem('auditUserEmail') || ''
  };
}

export function setAuditUser(name, email) {
  localStorage.setItem('auditUserName', name);
  localStorage.setItem('auditUserEmail', email);
}

// The sign-in token (an ID token) lasts about an hour. MSAL hands back the cached one even when it has
// just run out, so a page left open past the hour got "Invalid or expired token" on its next request.
// A token that is expired, or about to be, is renewed first (one renewal shared by simultaneous requests).
const TOKEN_SCOPES = ['openid', 'profile', 'email'];
const RENEW_WITHIN_MS = 5 * 60 * 1000;
let renewing = null;

async function getAccessToken({ forceRefresh = false } = {}) {
  try {
    if (!msalInstance) return null;
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    let response = await msalInstance.acquireTokenSilent({ scopes: TOKEN_SCOPES, account: accounts[0], forceRefresh });
    const exp = response.idTokenClaims && response.idTokenClaims.exp;
    const stale = exp && exp * 1000 - Date.now() < RENEW_WITHIN_MS;
    if (!forceRefresh && stale) {
      if (!renewing) {
        renewing = msalInstance
          .acquireTokenSilent({ scopes: TOKEN_SCOPES, account: accounts[0], forceRefresh: true })
          .finally(() => { renewing = null; });
      }
      response = await renewing;
    }
    return response.idToken;
  } catch (err) {
    console.error('Failed to acquire token:', err);
    return null;
  }
}

// Global fetch interceptor - automatically adds Bearer token to all API calls
let originalFetch = null;

function installFetchInterceptor() {
  if (originalFetch) return; // Already installed
  originalFetch = window.fetch;

  window.fetch = async function(url, options = {}) {
    // Only intercept calls to our API (not external calls or public endpoints)
    const urlStr = typeof url === 'string' ? url : url.toString();
    const isApiCall = urlStr.includes('/api/') && !urlStr.includes('/api/public/');

    if (isApiCall) {
      const headers = new Headers(options.headers || {});

      // Add Bearer token
      try {
        const token = await getAccessToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (err) {
        console.error('Token acquisition failed:', err);
      }

      // Add audit user headers
      const user = getAuditUser();
      if (user.name) headers.set('X-Audit-User-Name', user.name);
      if (user.email) headers.set('X-Audit-User-Email', user.email);

      const response = await originalFetch.call(window, url, { ...options, headers });
      // The server says the token has expired: get a fresh one and try the same request once more.
      if (response.status === 401 && !options.__retriedWithFreshToken) {
        let expired = false;
        try { expired = (await response.clone().json()).error === 'Invalid or expired token'; } catch { /* not JSON */ }
        if (expired) {
          const fresh = await getAccessToken({ forceRefresh: true });
          if (fresh) {
            headers.set('Authorization', `Bearer ${fresh}`);
            return originalFetch.call(window, url, { ...options, headers, __retriedWithFreshToken: true });
          }
        }
      }
      return response;
    }

    // Non-API calls pass through unchanged
    return originalFetch.call(window, url, options);
  };
}

// Named export for backward compatibility (pages that import auditFetch directly)
export async function auditFetch(url, options = {}) {
  // With the global interceptor installed, this just delegates to fetch
  // which will automatically add the token
  return fetch(url, options);
}
